'use client'

import { useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, Play, RotateCcw } from 'lucide-react'
import { GameLevel } from '../../index'
import { useGameEngine } from '../../utils/useGameEngine'
import { CharacterRenderer, GameUIRenderer, drawCollectEffect, drawJuliaWeddingLevel } from '../../utils/characterRenderer'
import { WeddingBackgroundRenderer, drawGift } from './weddingRenderer'
import FloatingEmoji from '../../../../ui/FloatingEmoji'

interface VeniceLevelProps {
level: GameLevel
onComplete: (levelId: number, score: number) => void
onBack: () => void
}

const WeddingLevel = ({ level, onComplete, onBack }: VeniceLevelProps) => {
const canvasRef = useRef<HTMLCanvasElement>(null)
const backgroundRenderer = useRef(new WeddingBackgroundRenderer())

// Détection du device (mobile ou desktop)
const isMobile = typeof window !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)

// Configuration harmonisée pour Wedding
const gameConfig = {
  playerSpeed: isMobile ? 1.2 : 1.2,
  targetSpeed: 1.3,
  accelerationMultiplier: isMobile ? 2.5 : 2.5,
  collectiblePoints: 100,
  victoryBonus: 100,
  jumpHeight: isMobile ? 150 : 180,
  jumpSpeed: isMobile ? 4 : 6,
  collectibleSpawnRate: 0.08,
  obstacleSpawnRate: 2.5
  // winDistance reste par défaut (50)
}

// Utiliser le hook de moteur de jeu - Julia court après Ben !
const {
	gameStarted,
	gameOver,
	gameWon,
	score,
	gameState,
	startGame,
	jump,
	isRunning,
	updateCanvasWidth,
	setDrawCallback
} = useGameEngine({
	config: gameConfig,
	playerStartX: 10, // Julia commence à gauche
	targetStartX: 200,  // Ben commence à droite
	accelerationKey: 'ArrowRight', // Julia accélère vers la droite pour rattraper Ben
	jumpKey: 'Space',
	onGameWon: (finalScore) => {
	setTimeout(() => {
		onComplete(level.id, finalScore)
	}, 2000)
	}
})


// Fonction de dessin spécialisée pour le mariage
const draw = useCallback(() => {
	const canvas = canvasRef.current
	if (!canvas) return

	const ctx = canvas.getContext('2d')
	if (!ctx) return

	ctx.save()
	if (gameState.camera.shouldFollow) {
	ctx.translate(-gameState.camera.x, 0)
	}
	backgroundRenderer.current.drawBackground(ctx,
	gameState.camera.shouldFollow ? gameState.camera.x : 0,
	canvas.width, canvas.height)
	backgroundRenderer.current.drawGround(ctx,
	gameState.camera.shouldFollow ? gameState.camera.x : 0,
	canvas.width, canvas.height)

	// Écrans de jeu
	if (!gameStarted && !gameOver && !gameWon) {
	ctx.restore()
	GameUIRenderer.drawStartScreen(ctx, canvas.width, canvas.height,
		'� WEDDING RUN �',
		'Julia rejoint Ben pour le grand OUI !',
		['➡️ Flèche droite pour accélérer', 'ESPACE pour sauter']
	)
	return
	}
	if (gameWon) {
	GameUIRenderer.drawVictoryScreen(ctx, gameState.camera.x, gameState.camera.shouldFollow,
		canvas.width, canvas.height, gameState.score,
		'🎉 MARIAGE ! 🎉',
		'Julia et Ben sont réunis !',
		'💕 Vive les mariés ! 💕'
	)
	if (!gameState.camera.shouldFollow) ctx.restore()
	return
	}
	if (gameOver) {
	GameUIRenderer.drawGameOverScreen(ctx, gameState.camera.x, gameState.camera.shouldFollow,
		canvas.width, canvas.height, gameState.score)
	if (!gameState.camera.shouldFollow) ctx.restore()
	return
	}

	// Dessiner les personnages - Julia en robe de mariée !
	if (gameState.hugAnimation.active) {
	CharacterRenderer.drawHuggingCouple(ctx,
		gameState.player.x, gameState.player.y - gameState.player.jumpHeight,
		gameState.target.x, gameState.target.y - gameState.target.jumpHeight,
		gameState.hugAnimation.time)
	} else {
	// Julia en robe de mariée
	drawJuliaWeddingLevel(ctx,
		gameState.player.x,
		gameState.player.y - gameState.player.jumpHeight,
		isRunning,
		gameState.runningTime)
	// Ben (groom)
	CharacterRenderer.drawBen(ctx,
		gameState.target.x,
		gameState.target.y - gameState.target.jumpHeight,
		true,
		gameState.runningTime,
		false)
	}


	// Obstacles : bonhommes de neige
	gameState.obstacles.forEach(obs => {
	drawGift(ctx, obs.x, obs.y, obs.width, obs.height)
	})
	// Collectibles : cadeaux (style original)
	gameState.collectibles.forEach(collectible => {
	if (!collectible.collected) {
		// Dessin d'un cadeau pixel art (reprendre l'ancien visuel)
		// --- DESSIN CADEAU ---
		ctx.save();
		const x = collectible.x;
		const y = collectible.y;
		const w = 20;
		const h = 20;
		// Boîte
		ctx.fillStyle = '#e74c3c';
		ctx.fillRect(x + 2, y + 6, w - 4, h - 8);
		// Ruban vertical
		ctx.fillStyle = '#f9e79f';
		ctx.fillRect(x + w / 2 - 2, y + 6, 4, h - 8);
		// Ruban horizontal
		ctx.fillRect(x + 2, y + h / 2, w - 4, 4);
		// Noeud
		ctx.fillStyle = '#f7ca18';
		ctx.beginPath();
		ctx.arc(x + w / 2 - 4, y + 6, 3, 0, Math.PI * 2);
		ctx.arc(x + w / 2 + 4, y + 6, 3, 0, Math.PI * 2);
		ctx.fill();
		ctx.restore();
	}
})

// Dessiner les pièces (coins)
gameState.coins?.forEach(coin => {
  if (!coin.collected) {
	ctx.save()
	ctx.beginPath()
	ctx.arc(coin.x + 10, coin.y + 10, 10, 0, 2 * Math.PI)
	ctx.fillStyle = '#FFD700'
	ctx.shadowColor = '#F9E79F'
	ctx.shadowBlur = 8
	ctx.fill()
	ctx.restore()
  }
	})

	// Dessiner les effets visuels de collecte
	gameState.collectEffects.forEach(effect => {
	const isHugEffect = gameState.hugAnimation.active && gameState.hugAnimation.heartsSpawned
	drawCollectEffect(ctx, effect.x, effect.y, effect.opacity, effect.scale, isHugEffect)
	})

	// Restaurer le contexte pour l'interface (fixe à l'écran)
	ctx.restore()


	// Interface utilisateur adaptée mariage
ctx.fillStyle = '#000'
ctx.font = 'bold 20px Arial'
ctx.textAlign = 'left'
ctx.fillText(`Score: ${gameState.score}`, 10, 30)
ctx.fillText(`Distance: ${Math.round(gameState.distance)}m`, 10, 60)

}, [gameStarted, gameOver, gameWon, gameState, isRunning])

// Setup initial du canvas
useEffect(() => {
	const canvas = canvasRef.current
	if (!canvas) return

	canvas.width = 800
	canvas.height = 400
	updateCanvasWidth(canvas.width)
}, [updateCanvasWidth])

// Enregistrer la fonction de dessin pour le rendu automatique
useEffect(() => {
	setDrawCallback(draw)
}, [draw, setDrawCallback])

// Gestionnaire de clic pour le saut et le démarrage du jeu
const handleCanvasClick = () => {
	if (!gameStarted && !gameOver && !gameWon) {
	startGame()
	} else if (gameStarted && !gameOver && !gameWon) {
	jump()
	} else if (gameOver || gameWon) {
	startGame()
	}
}

return (
	<motion.div
	initial={{ opacity: 0, y: 20 }}
	animate={{ opacity: 1, y: 0 }}
	transition={{ duration: 0.6 }}
	className="space-y-6"
	>
	{/* Header */}
	<div className="flex flex-col items-center w-full mt-2 mb-6">
		<h2 className="text-4xl font-serif font-bold text-bordeaux-700 max-w-lg mx-auto flex items-center justify-center gap-2">
			<span className="text-xl"></span>
			Niveau final
		</h2>
		<div className="mt-3 text-center text-xl text-bordeaux-700 max-w-md mx-auto">
			<span className="font-medium">Aide Julia et Ben à se marier !</span>
		</div>
	</div>
	{/* Zone de jeu avec animation festive d'emojis flottants */}
	<div className="card bg-white relative flex flex-col items-center justify-center" style={{ minHeight: '420px' }}>
		{/* Animation emojis flottants au-dessus du canvas */}
		<FloatingEmoji className="w-full h-full" />
		<div className="relative w-full flex flex-col items-center">
		<canvas
			ref={canvasRef}
			className="w-full border border-gray-300 rounded-lg cursor-pointer"
			style={{ maxWidth: '800px', height: '400px' }}
			onClick={handleCanvasClick}
		/>
		{/* Bouton commencer centré sur le canvas au début */}
		{!gameStarted && !gameOver && !gameWon && (
			<>
			<span className="absolute left-1/2 top-[10%] transform -translate-x-1/2 text-sm text-bordeaux-700 font-medium" style={{ zIndex: 10 }}>
				<p>Comment jouer </p>
			</span>
			<button
			onClick={startGame}
			className="btn-primary flex items-center gap-2 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow-lg text-lg px-6 py-3"
			style={{ zIndex: 10 }}
			>
			<Play className="w-5 h-5" />
			Commencer le niveau
			</button>
			<span className="absolute left-1/2 top-[78%] transform -translate-x-1/2 text-sm text-bordeaux-700 font-medium" style={{ zIndex: 10 }}>
				<p>⛄️ Attention aux bonhomes de neige !</p>
			</span>
			</>
		)}
		</div>
	</div>

	{/* Boutons de fin de partie */}
	<div className="flex justify-center gap-4 mt-2">
		{gameOver && !gameWon && (
		<button
			onClick={startGame}
			className="btn-primary flex items-center gap-2"
		>
			<RotateCcw className="w-4 h-4" />
			Rejouer
		</button>
		)}

		{gameWon && (
		<div className="text-center text-green-600 font-semibold">
			Passage au niveau suivant...
		</div>
		)}
	</div>
	</motion.div>
)
}

export default WeddingLevel
